import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_HELP_ARTICLE,
  getHelpArticleByCategory,
  getHelpArticleById,
  type HelpArticle,
} from '../data/helpRegistry';

type HelpContextValue = {
  isOpen: boolean;
  article: HelpArticle;
  openHelp: (articleId?: string, category?: string) => void;
  closeHelp: () => void;
};

const HelpContext = createContext<HelpContextValue | undefined>(undefined);

export function HelpProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [article, setArticle] = useState<HelpArticle>(DEFAULT_HELP_ARTICLE);

  const openHelp = useCallback((articleId?: string, category?: string) => {
    const resolvedArticle = getHelpArticleById(articleId)
      ?? getHelpArticleByCategory(category)
      ?? DEFAULT_HELP_ARTICLE;

    setArticle(resolvedArticle);
    setIsOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo(() => ({
    isOpen,
    article,
    openHelp,
    closeHelp,
  }), [article, closeHelp, isOpen, openHelp]);

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelpContext() {
  const context = useContext(HelpContext);

  if (!context) {
    throw new Error('useHelp must be used within a HelpProvider');
  }

  return context;
}
